/**
 * useDraftSession — custom hook encapsulating the draft session pipeline.
 * Owns: draftSession, liveCanvasText, inPlaceEditState, zoomPickingPrompt.
 * Extracted from App.tsx — pure structural refactor.
 */

import { useRef, useState } from "react";

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  extractPageSetup,
  type DrawingDocument,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";

import { excludeControlViewport } from "../scene3d/viewports/viewportControlFilter";
import { startDraftSession } from "../scene/draftingHelpers";
import {
  advanceDraftSessionWithPoint,
  advanceDraftSessionWithText,
} from "../scene/draftStateMachine";
// REVCLOUD-MODES — Freehand clouds follow the GUIDED cursor (AutoCAD
// draws as the crosshairs move, no clicks) — hover updates advance the
// sampled path and auto-close when the cursor returns to the start.
import { advanceRevcloudFreehandHover } from "../scene/drafting/revcloudDraft";
// Point-filter / temporary-tracking (FROM/M2P/.X.Y.Z — ТЗ §8.6/§10.4): a
// typed command-line "ESC" must cancel just the active filter sub-prompt,
// not the whole draft (Codex PR #504 P3).
import {
  clearPointFilter,
  isPointFilterActive,
} from "../scene/pointFilterSession";
// OTRACK-SUBSYSTEM `TT` sub-prompt — local Codex review round 10 P2 ("Route
// typed ESC through TT cancellation"): mirrors the point-filter check
// immediately above for the SAME third ESC entry point (`useKeyboardShortcuts
// .ts`'s canvas Escape already has the TT-vs-point-filter precedence right;
// this typed command-line path did not).
import {
  clearTemporaryTrackPoint,
  isTemporaryTrackPointActive,
} from "../scene/otrackTrackPointSession";
import type {
  DraftCommitResult,
  DraftCommandId,
  DraftPoint,
  DraftSession,
} from "../scene/draftTypes";
import {
  getEntityById,
  selectActiveSpace,
  useEditorStore,
} from "../state/editorStore";
import { useSpaceContextStore } from "../state/spaceContextStore";
import { useSysvarStore } from "../dynamicInput/sysvarStore";
import { useT } from "../i18n";
import { useStageHoverStore } from "../state/hoverState";
import {
  getPreviewPreferences,
  PREVIEW_FILTER_LOCKED_LAYER,
  PREVIEW_FILTER_TABLES,
  PREVIEW_FILTER_XREFS,
} from "../state/previewPreferences";

/**
 * AutoCAD PREVIEWFILTER bit 1 helper — looks up the layer in the
 * current document and returns true when its `locked` flag is set.
 *
 * Reads from the live `useEditorStore` snapshot so the gate stays in
 * sync with LAYER ON/OFF/LOCK toggles without a re-subscribe path.
 * Returns false when the layer is unknown — unknown layers fall back
 * to layer "0" semantics in the renderer (never locked) so we stay
 * consistent with that.
 */
function isLayerLocked(layerName: string): boolean {
  const layers =
    useEditorStore.getState().geometryDocument?.styleTables?.layers ?? [];
  for (const l of layers) {
    if (l.name === layerName) return l.locked === true;
  }
  return false;
}

/**
 * AutoCAD PREVIEWFILTER bit 2 helper. We don't yet model XREF as a
 * distinct entity type — externally referenced drawings are flattened
 * to their constituent INSERT records on import. Until the XREF type
 * lands (P12 plan), we return false — matching AutoCAD parity for a
 * file that doesn't currently bind XREFs.
 */
function isXrefEntity(_entity: { type: string }): boolean {
  return false;
}

/**
 * AutoCAD PREVIEWFILTER bit 4 helper. ACAD_TABLE entities are not yet
 * modelled (Phase Q.2 plan: full ACAD_TABLE support, deferred). The
 * filter is wired so the moment the type is added the gate already
 * works — for now we return false.
 */
function isTableEntity(_entity: { type: string }): boolean {
  return false;
}

/**
 * Document-frame origin `defaultPageSetup()` carries — the substitute a
 * pixel-unit layout's frame is drawn at. Named rather than inlined so the
 * branch below reads as "return the document-frame default", not
 * "return zero": `Scene3DStage`'s own pixel-unit fallback returns the
 * OFFSET-world image of this same document value, and the two must stay
 * visibly distinguishable.
 */
const DEFAULT_PAGE_SETUP_ORIGIN = { x: 0, y: 0 } as const;

/**
 * VPCLIP-INTERACTIVE — resolve the `LayoutPageSetup.origin` for the space a
 * draft point/text advance is running against, or `null` when the space has
 * no page setup (non-layout space, or a layout whose LAYOUT object carries
 * no usable page-size data). Threaded into `advanceDraftSessionWithPoint`/
 * `advanceDraftSessionWithText` as `paperOrigin` so `vpclip-select-viewport`
 * can hit-test against the SAME `pointInViewport(paperPt, vp, paperOrigin)`
 * choke point `viewportContextController.ts`'s double-click-to-enter-VP pick
 * already uses.
 *
 * # Coordinate frame — DOCUMENT, because the draft machine is document-frame
 *
 * The returned origin is the RAW `LayoutPageSetup.origin` (DXF group 10/20
 * `minimumLimits`), i.e. the value AutoCAD shows — NOT the renderer's
 * OFFSET-world image of it. The name says the frame because a second,
 * DIFFERENT paper-origin value exists for the other frame:
 * `Scene3DStage.tsx`'s `paperOriginOffset` memo
 * (`coordinateOrigin.applyOffsetToPaperOrigin`), which feeds the sheet mesh,
 * the VP frame meshes, the GL scissor rects, the layout auto-fit camera and
 * the pointer-side VP redirect. Both are correct; they serve opposite sides
 * of one boundary and must never be swapped.
 *
 * The boundary itself is `pickLoop/interactivePickLoop.ts`: it converts the
 * pointer coordinate to the document frame through
 * `ActiveInputSpace.toDocument` at the `onCanvasPoint` / `onCanvasHover`
 * emits, so by the time a `point` reaches the draft machine it is a DOCUMENT
 * coordinate. `ctx.paperOrigin` must therefore be document too — both
 * operands of `pointInViewport(point, vp, paperOrigin)` and of the
 * `point − paperOrigin` sheet-local commits (`vpclipRegistry.ts`'s
 * `vpclip-second-corner`, `advanceDispatch.ts`'s `viewport-second-corner`)
 * live in one frame, which is the precondition all of that arithmetic
 * depends on.
 *
 * History: commit `3e75105` (paper space joins the one render frame) made
 * this function OFFSET-world, which was correct while the draft machine
 * itself was offset-world. Moving the machine to the document frame makes it
 * wrong again, so that one function is reverted here — the rest of `3e75105`
 * (every renderer-side consumer of `paperOriginOffset`) stands unchanged.
 *
 * Note what does NOT change either way: `vpclip-second-corner` /
 * `viewport-second-corner` commit `point − paperOrigin`, and a DIFFERENCE of
 * two paper quantities is invariant under the pure translation that
 * separates the two frames (`(p − O) − (origin − O) === p − origin`), so the
 * persisted sheet-local values are the same document numbers under either
 * convention — provided BOTH operands share one frame, which is exactly what
 * this function guarantees for the draft machine.
 *
 * Mirrors `Scene3DStage.tsx`'s own `activeLayout` derivation EXACTLY
 * (@codex round-2 P2): `layoutsBySpaceId[spaceId]` is the primary lookup,
 * but legacy documents / merge paths that have not yet rebuilt it fall
 * back to the by-NAME `layouts` dict, gated on the space actually being a
 * `"layout"` kind — using only the by-id lookup left a legacy document
 * (or any merge path that hasn't populated `layoutsBySpaceId` yet) hit-
 * testing at origin {0,0} against a viewport the renderer draws at a
 * non-zero `minimumLimits` offset.
 */
export function resolveDraftPaperOriginDocument(
  geometryDocument: Pick<
    DrawingDocument,
    "layoutsBySpaceId" | "layouts" | "spaces"
  >,
  spaceId: string,
): { x: number; y: number } | null {
  const space = geometryDocument.spaces.find((s) => s.id === spaceId);
  if (!space || space.kind !== "layout") return null;
  const layout =
    geometryDocument.layoutsBySpaceId[spaceId] ??
    geometryDocument.layouts[space.name] ??
    null;
  const setup = extractPageSetup(layout);
  // Mirrors Scene3DStage's own `paperPageSetup` fallback (@codex round-1
  // P3): a pixel-unit layout (plotPaperUnits=2) cannot be placed in world
  // space, so the renderer substitutes `defaultPageSetup()` — origin
  // {0,0} — for its frame instead of the raw extracted `minimumLimits`
  // offset. VPCLIP's hit-test/point-projection must resolve the SAME
  // origin the frame is actually drawn at, or a pixel-unit layout with a
  // non-zero minimumLimits desyncs the pick math from the visible frame.
  // The substituted {0,0} is already a DOCUMENT-frame origin (that is what
  // `defaultPageSetup()` carries), so it is returned as-is, exactly like the
  // extracted one — this function hands the draft machine document values.
  if (setup?.units === "pixels") return { ...DEFAULT_PAGE_SETUP_ORIGIN };
  return setup?.origin ? { x: setup.origin.x, y: setup.origin.y } : null;
}

/**
 * MC4 iter-2 P1 #165 (extended @codex round-1 P2) — resolve which space a
 * newly-started draft session should target.
 *
 * In `MSPACE_VIA_VP` (the user has double-clicked INTO a floating
 * viewport) the underlying `drawing.activeSpaceId` stays the LAYOUT space,
 * but most commands must target MODEL space so entities land where the
 * user is actually looking, through the viewport. Two commands are
 * exempt from that redirect because they operate on paper-space Viewport
 * ENTITIES themselves, not on whatever the viewport happens to be
 * displaying:
 *   - MVIEW creates a new Viewport entity in the layout space.
 *   - VPCLIP reshapes an EXISTING Viewport entity's paper-space rectangle.
 *     Without this exemption, `vpclip-select-viewport`'s hit-test would
 *     scan MODEL entities (no Viewport type there — every click silently
 *     misses) and a pre-armed viewport's corner picks would commit
 *     MODEL-space coordinates as the paper-space `bounds`, reshaping the
 *     real viewport incorrectly.
 *
 * The `activeSpaceKind !== "layout"` fallback (no layout in view at all —
 * e.g. launched from the Model TAB, TILEMODE=1) mirrors MVIEW's own
 * auto-target-first-layout behaviour: AutoCAD parity, since VPCLIP — like
 * MVIEW — only operates on paper-space viewports and has nothing to
 * act on without one. `firstLayoutSpaceId` is the caller-resolved id of
 * the document's first REAL layout space (@codex round-2 P1 — a bare
 * `"layout-1"` literal only matches a synthetic/test fixture; an actual
 * imported DXF's layout space id is generated as `"layout:<name>"`, so
 * hard-coding `"layout-1"` targeted a space that does not exist and left
 * the session unable to activate or find its viewport). Falls back to
 * `activeSpaceId` (stays put — nothing to act on) when the document
 * genuinely has no layout space at all.
 *
 * Pure + exported so the MSPACE_VIA_VP/no-layout matrix is directly unit-
 * testable without mocking the full hook + its stores.
 */
export function resolveDraftTargetSpaceId(
  command: DraftCommandId,
  spaceContextMode: string,
  activeSpaceKind: string,
  activeSpaceId: string,
  firstLayoutSpaceId: string | null,
): string {
  const isViewportSpaceCommand = command === "MVIEW" || command === "VPCLIP";
  if (spaceContextMode === "MSPACE_VIA_VP" && !isViewportSpaceCommand) {
    return "model";
  }
  if (isViewportSpaceCommand && activeSpaceKind !== "layout") {
    return firstLayoutSpaceId ?? activeSpaceId;
  }
  return activeSpaceId;
}

export type UseDraftSessionOptions = {
  appendCommandFeed: (text: string) => void;
  setCommandText: (value: string | ((prev: string) => string)) => void;
  setCommandHistoryOpen: (
    value: boolean | ((prev: boolean) => boolean),
  ) => void;
  formatCommandPoint: (point: DraftPoint) => string;
  /**
   * Optional interceptor for geometry-mutating commands. When it returns
   * `true`, the command is consumed by the interceptor (e.g. stashed for
   * a pending confirmation dialog) and the draft session SKIPS the
   * normal dispatch. When it returns `false` / `undefined` the usual
   * dispatch path runs.
   *
   * Used by the P7 overlap warning dialog to defer
   * `geometry/create-block-insert` until the user confirms.
   */
  interceptGeometryDispatch?: (command: EditorCommand) => boolean;
};

export function useDraftSession(options: UseDraftSessionOptions) {
  const {
    appendCommandFeed,
    setCommandText,
    setCommandHistoryOpen,
    formatCommandPoint,
    interceptGeometryDispatch,
  } = options;

  const t = useT();
  const state = useEditorStore((store) => store.state);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);
  const dispatch = useEditorStore((store) => store.dispatch);
  const appendShellEntity = useEditorStore((store) => store.appendShellEntity);

  const [draftSession, setDraftSession] = useState<DraftSession | null>(null);
  // Ref mirror of draftSession — updated on every render so that
  // handleDraftTextSubmission always reads the latest committed value
  // instead of a stale closure capture (critical for editorRunBatch).
  const draftSessionRef = useRef<DraftSession | null>(null);
  draftSessionRef.current = draftSession;
  const [liveCanvasText, setLiveCanvasText] = useState("");
  const [inPlaceEditState, setInPlaceEditState] = useState<{
    entityId: string;
    text: string;
  } | null>(null);
  const [zoomPickingPrompt, setZoomPickingPrompt] = useState<string | null>(
    null,
  );

  const activeSpace = selectActiveSpace(state);

  /**
   * Intercept `geometry/remove-leader-from-multileader` before
   * dispatch to handle the "last leader" confirmation dialog. Also
   * runs the caller-supplied `interceptGeometryDispatch` predicate
   * (used by the P7 overlap warning dialog) which can consume the
   * command and defer its dispatch.
   */
  function dispatchWithMleaderEditGuards(geometryCommand: EditorCommand): void {
    // Freeze the live MIRRTEXT sysvar onto a MIRROR command at dispatch time
    // (AutoCAD captures the sysvar when the command executes). The draft state
    // machine is a pure module with no store access, so the value is stamped
    // here. Default 0 in the contract keeps text readable if it is ever absent.
    if (geometryCommand.type === "geometry/mirror-entities") {
      geometryCommand = {
        ...geometryCommand,
        mirrtext: useSysvarStore.getState().values.MIRRTEXT,
      };
    }
    // CECOLOR — the live current colour is stamped onto stampable create
    // commands at the SINGLE dispatch chokepoint in `editorStore.dispatch`
    // (`stampCeColorOntoCreateCommand`), so the interactive draft, the
    // command-line text path, and the AI contour all inherit CECOLOR through one
    // seam. No per-path stamp is needed here.
    if (
      interceptGeometryDispatch &&
      interceptGeometryDispatch(geometryCommand)
    ) {
      return;
    }
    if (
      geometryCommand.type === "geometry/remove-leader-from-multileader" &&
      !geometryCommand.confirmed
    ) {
      const targetGeomEntity = geometryDocument.spaces
        .flatMap((space) => space.entities)
        .find((entity) => entity.id === geometryCommand.entityId);
      if (targetGeomEntity && targetGeomEntity.type === "Multileader") {
        const totalLines = targetGeomEntity.leaderRoots.reduce(
          (sum, root) =>
            sum +
            root.nodes.reduce(
              (nodeSum, node) => nodeSum + node.lines.length,
              0,
            ),
          0,
        );
        if (totalLines <= 1) {
          const ok =
            typeof window !== "undefined"
              ? window.confirm(t("confirm.mleaderedit.last_leader"))
              : true;
          if (!ok) {
            appendCommandFeed(t("feed.esc"));
            return;
          }
          dispatch({ ...geometryCommand, confirmed: true });
          return;
        }
      }
    }
    dispatch(geometryCommand);
  }

  function commitDraftedEntity(result: DraftCommitResult) {
    // feedOnly commits (e.g. BEDIT-on-reserved-name rejection) just
    // surface a message in the command-line feed without dispatching
    // any geometry change or creating an entity. Used by validation
    // paths in the state machine that need to inform the user without
    // mutating the document. We ALSO push the rejection through the
    // audit log via a no-op commandline/submit so E2E tests and the
    // command transcript carry a record of why the action was
    // refused.
    if (result.feedOnly) {
      appendCommandFeed(result.summary);
      dispatch({
        type: "commandline/submit",
        source: "scene",
        text: result.summary,
      });
      return;
    }

    if (result.geometryCommands?.length) {
      for (const geometryCommand of result.geometryCommands) {
        dispatchWithMleaderEditGuards(geometryCommand);
      }
      return;
    }

    if (result.geometryCommand) {
      dispatchWithMleaderEditGuards(result.geometryCommand);
      return;
    }

    if (result.entity) {
      appendShellEntity({
        entity: result.entity,
        summary: result.summary,
        source: "scene",
        commandText: result.commandText,
      });
    }
  }

  /**
   * DRAFT-DOCUMENT-FRAME — `point` is a DOCUMENT (AutoCAD/DXF) coordinate,
   * not a render-frame one. `clientToWorld` unprojects against the
   * OFFSET-world camera, but `pickLoop/interactivePickLoop.ts` converts back
   * at the `onCanvasPoint` emit through `ActiveInputSpace.toDocument`, which
   * is the one place that knows whether the pointer went through the
   * identity space (offset-world, un-offset it) or was redirected into a
   * viewport (`paperToModel` already returns a document model point). The
   * same holds for `handleStageHover`'s point → `session.hoverPoint`.
   *
   * So everything below this line — `session.points`, `ctx.paperOrigin`
   * (`resolveDraftPaperOriginDocument`), the `existingEntities` /
   * `geometryEntities` the machine hit-tests against, every geometry command
   * it commits, and the `formatCommandPoint` echo into the command feed — is
   * in one frame, the one the user reads in AutoCAD.
   */
  function handleDraftPoint(
    point: DraftPoint,
    source: EditorCommand["source"],
  ) {
    // Guard against corrupted coordinates from touch event synthesis.
    // Touch-to-mouse conversion on some devices can produce NaN/Infinity
    // which would break the state machine and cause an infinite render loop.
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      return;
    }

    // Read the LATEST draftSession from the ref instead of the
    // closure-captured value or a `setDraftSession` updater argument.
    // The ref is updated on every render (see `draftSessionRef.current =
    // draftSession`), so it always reflects the most recent committed
    // state — this mirrors the proven `handleDraftTextSubmission` path.
    //
    // The previous implementation ran the whole advance + commit inside a
    // `setDraftSession((currentSession) => { ... })` functional updater and
    // dispatched the side-effecting `commitDraftedEntity` from within it.
    // That is a React anti-pattern: under React 18 batching / StrictMode the
    // updater can run twice (double-dispatching the commit) or the commit
    // can be dropped, and the second of two rapid picks (e.g. MVIEW's two
    // corners) could read a stale session and fail to commit. Multi-step
    // commits such as MVIEW's `viewport-second-corner` create-viewport were
    // silently lost through the mouse path while the typed path — which uses
    // the ref + side-effects-outside-setState pattern below — worked.
    const currentSession = draftSessionRef.current;
    if (!currentSession) {
      dispatch({
        type: "shell/clear-selection",
        source,
      });
      return;
    }

    appendCommandFeed(formatCommandPoint(point));

    // Lift the active geometry-space entities so the state machine
    // can run boundary detection for HATCH internal-pick. Other
    // steps ignore the parameter — see `advanceDraftSessionWithPoint`.
    const activeGeoSpaceForPoint = geometryDocument.spaces.find(
      (s) => s.id === currentSession.spaceId,
    );
    const result = advanceDraftSessionWithPoint(
      currentSession,
      point,
      state.drawing.entities,
      currentSession.spaceId,
      state.propertyWorkflows.defaultObjectProperties,
      activeGeoSpaceForPoint?.entities ?? [],
      resolveDraftPaperOriginDocument(geometryDocument, currentSession.spaceId),
    );

    if (result.commit) {
      commitDraftedEntity(result.commit);
    }

    // Keep the ref in sync immediately so a second pick that fires in the
    // same tick (before React flushes the re-render that refreshes the
    // ref) reads the advanced session rather than the previous one.
    draftSessionRef.current = result.nextSession;
    setDraftSession(result.nextSession);
  }

  function handleDraftTextSubmission(rawText: string) {
    // Read the LATEST draftSession from the ref instead of the
    // closure-captured value. The ref is updated on every render
    // (see draftSessionRef.current = draftSession below), so it
    // always reflects the most recent committed state — critical for
    // editorRunBatch where commands fire in rapid succession and the
    // closure may be stale.
    const currentSession = draftSessionRef.current;
    if (!currentSession) {
      return false;
    }

    if (rawText.toUpperCase() === "ESC") {
      appendCommandFeed(t("feed.esc"));
      // Point-filter / temporary-tracking (FROM/M2P/.X.Y.Z): a command-line
      // submission of the literal word "ESC" must cancel just the active
      // filter sub-prompt and return to the host command's own point prompt
      // (AutoCAD parity) — not abort the whole draft. This check MUST run
      // before the unconditional `setDraftSession(null)` below: this "ESC"
      // word-form branch is checked first, ahead of any registry/session
      // text dispatch, so `advancePointFilterWithText`'s own `\x1b`/"ESC"
      // handling (pointFilterSession.ts) never gets a chance to run for a
      // TYPED command-line "ESC" + Enter — only for the DI-keystroke
      // `\x1b` path, which submits through a different route. Mirrors the
      // canvas Escape fix (useKeyboardShortcuts.ts) and the command-input
      // Escape fix (App.tsx) for the third ESC entry point. (Codex PR #504
      // P3 — "Route typed ESC before the global draft abort".)
      // OTRACK-SUBSYSTEM (local Codex review round 10 P2) — checked FIRST,
      // matching the canvas Escape handler's (`useKeyboardShortcuts.ts`)
      // TT-before-point-filter precedence and the state machines' own
      // TT-before-point-filter interception order (`interceptTemporaryTrack
      // PointText` sits ahead of `interceptPointFilterText` at every call
      // site). Without this, a typed "ESC" during an active TT sub-prompt
      // fell through past BOTH branches straight to the unconditional
      // `setDraftSession(null)` below, aborting the entire host command
      // instead of cancelling just the TT sub-prompt.
      if (isTemporaryTrackPointActive(currentSession)) {
        setDraftSession(clearTemporaryTrackPoint(currentSession));
        return true;
      }
      if (isPointFilterActive(currentSession)) {
        setDraftSession(clearPointFilter(currentSession));
        return true;
      }
      setDraftSession(null);
      return true;
    }

    if (rawText.trim()) {
      appendCommandFeed(rawText.trim());
    }

    const activeGeoSpaceForText = geometryDocument.spaces.find(
      (s) => s.id === currentSession.spaceId,
    );
    const result = advanceDraftSessionWithText(
      currentSession,
      rawText,
      state.drawing.entities,
      currentSession.spaceId,
      state.propertyWorkflows.defaultObjectProperties,
      activeGeoSpaceForText?.entities ?? [],
      resolveDraftPaperOriginDocument(geometryDocument, currentSession.spaceId),
    );

    if (result.commit) {
      commitDraftedEntity(result.commit);
    }

    setDraftSession(result.nextSession);
    return true;
  }

  function beginDrafting(
    command: DraftCommandId,
    cmdOptions?: Record<string, string>,
  ) {
    // ── BEDIT / BCLOSE / BSAVE (Phase 2.3) — short-circuit cases ──
    // BSAVE dispatches immediately (no prompt) and stays in the
    // current BEDIT session. BCLOSE with no modifications also
    // dispatches immediately (saves = no-op, then exits). BEDIT arms
    // the session with the list of known block names so the state
    // machine can short-circuit the confirm-create step when the
    // user types an existing name.
    const activeGeoSpace = geometryDocument.spaces.find(
      (s) => s.id === geometryDocument.activeSpaceId,
    );
    const inBlockEdit = activeGeoSpace?.kind === "block-edit";
    if (command === "BSAVE") {
      if (!inBlockEdit) {
        appendCommandFeed(t("toast.bedit.not_in_block_editor"));
        return;
      }
      dispatch({ type: "space/save-block-edit", source: "toolbar" });
      appendCommandFeed(
        t("summary.bsave", { name: activeGeoSpace?.blockEditName ?? "" }),
      );
      return;
    }
    // ── ATTDEF (Phase 2.1) — must be inside BEDIT ──
    // Refuse the command and surface a toast instead of entering the
    // session. The dialog only opens when the state machine advances to
    // `attdef-dialog`; gating here prevents the session from starting.
    if (command === "ATTDEF") {
      if (!inBlockEdit) {
        appendCommandFeed(t("toast.attdef.not_in_block_editor"));
        return;
      }
    }

    if (command === "BCLOSE") {
      if (!inBlockEdit) {
        appendCommandFeed(t("toast.bedit.not_in_block_editor"));
        return;
      }
      const blockName = activeGeoSpace?.blockEditName ?? "";
      const blockDef = blockName
        ? geometryDocument.blocks[blockName]
        : undefined;
      // Compare current blockDef to the captured snapshot — if they
      // match byte-for-byte, nothing has changed and BCLOSE is
      // effectively a no-op save.
      const currentJson = blockDef ? JSON.stringify(blockDef) : "";
      const snapshot =
        typeof activeGeoSpace?.blockEditSnapshot === "string"
          ? activeGeoSpace.blockEditSnapshot
          : "";
      if (currentJson === snapshot) {
        dispatch({
          type: "space/exit-block-edit",
          source: "toolbar",
          action: "save",
        });
        appendCommandFeed(t("summary.bclose_saved", { name: blockName }));
        return;
      }
      // Modifications exist — open the confirm prompt. We seed the
      // session options with the block name for the prompt template.
      setDraftSession({
        command: "BCLOSE",
        step: "bclose-confirm",
        spaceId: activeGeoSpace?.id ?? state.drawing.activeSpaceId,
        points: [],
        hoverPoint: null,
        closed: false,
        options: { blockName },
      });
      setCommandText("");
      setCommandHistoryOpen(false);
      appendCommandFeed(command);
      dispatch({
        type: "commandline/submit",
        source: "toolbar",
        text: command,
        // Bare-command-entry audit-tone fix — this is a session-start audit
        // echo ("BCLOSE"), not raw command-line text to resolve; without
        // `resolvedTone` it fell through to the generic LINE/POINT/CIRCLE/ARC
        // resolver's not-wired fallback and was wrongly logged `warning`
        // ("audit.command_not_wired") even though BCLOSE's confirm prompt
        // legitimately started. See `commandline/submit.resolvedTone`'s doc
        // comment (packages/contracts/src/editor.ts).
        resolvedTone: "info",
      });
      return;
    }
    // VPCLIP-INTERACTIVE (@codex round-2 P1) — starting VPCLIP while
    // MSPACE_VIA_VP must force an exit back to PSPACE FIRST. Two
    // independent subsystems key off `spaceContextMode` while in that
    // mode: `resolveDraftTargetSpaceId` below (which space the SESSION
    // targets) and `Scene3DStage`'s own `effectiveInteractionSpaceId`
    // (which space every CANVAS POINT gets tagged with, unconditionally
    // routed to "model" in MSPACE_VIA_VP). The round-1 fix only taught
    // the first about VPCLIP's exemption — canvas points kept arriving
    // tagged "model", so `handleStagePoint`'s
    // `draftSession.spaceId === spaceId` guard rejected every VPCLIP
    // pick and the session never advanced. Exiting the viewport BEFORE
    // the session starts makes both subsystems agree (mode is PSPACE,
    // so neither redirects to "model") without touching camera/
    // unprojection code — the SAME transition an ESC/click-outside-VP
    // exit already exercises. Mirrors `viewportContextController.ts`'s
    // own `doExitViewport` in BOTH halves (@codex round-3 P2): the
    // `useSpaceContextStore` FSM AND the editorStore's
    // `drawing.layoutContext` (via `shell/set-layout-context "paper"`).
    // Updating only the former left `layoutContext` stuck at "viewport"
    // if the user cancelled the VPCLIP session before any other
    // shell/geometry dispatch normalised it — a user-visible desync
    // (AI dispatch context / UI routing) between the canvas, which really
    // is back in PSPACE, and the persisted editor state, which still
    // believed it was editing model-through-viewport.
    const spaceContextModeBeforeVpclipExit =
      useSpaceContextStore.getState().mode;
    if (
      command === "VPCLIP" &&
      spaceContextModeBeforeVpclipExit === "MSPACE_VIA_VP"
    ) {
      useSpaceContextStore.getState().exitViewport();
      dispatch({
        type: "shell/set-layout-context",
        source: "scene",
        layoutContext: "paper",
      });
    }
    // See `resolveDraftTargetSpaceId` (MC4 iter-2 P1 #165, extended
    // @codex round-1 P2 for VPCLIP-INTERACTIVE) for the MSPACE_VIA_VP /
    // no-layout targeting rules.
    const spaceContextMode = useSpaceContextStore.getState().mode;
    const firstLayoutSpaceId =
      state.drawing.spaces.find((space) => space.kind === "layout")?.id ?? null;
    const targetSpaceId = resolveDraftTargetSpaceId(
      command,
      spaceContextMode,
      activeSpace.kind,
      activeSpace.id,
      firstLayoutSpaceId,
    );
    const targetSpaceName =
      state.drawing.spaces.find((space) => space.id === targetSpaceId)?.name ??
      activeSpace.name;

    // MC4 iter-3 P1 #165 — In MSPACE_VIA_VP the draft targets model space but
    // the user must STAY visually in the layout tab.  Do NOT dispatch
    // shell/activate-space here: that would switch activeSpaceId → "model" and
    // pull the user out of the layout/VP view.  The draft session spaceId ("model")
    // is already threaded into startDraftSession below; only the tab activation
    // must be suppressed for this mode.
    if (
      targetSpaceId !== state.drawing.activeSpaceId &&
      spaceContextMode !== "MSPACE_VIA_VP"
    ) {
      dispatch({
        type: "shell/activate-space",
        source: "toolbar",
        spaceId: targetSpaceId,
        layoutContext: targetSpaceId === "model" ? "model" : "paper",
      });
    }

    // AutoCAD parity: pre-selection for modify commands.
    // If entities are selected when a modify command starts, pass them
    // as targetEntityIds so the state machine skips the "Select objects"
    // step and goes directly to parameter input (base point, angle, etc.)
    let resolvedOptions = cmdOptions;
    const MODIFY_COMMANDS_WITH_PRESELECTION = [
      "MOVE",
      "COPY",
      "MIRROR",
      "ROTATE",
      "SCALE",
      "STRETCH",
      "ARRAYRECT",
      "ARRAYPOLAR",
      "ARRAYPATH",
      // BLOCK (Phase 1) — when the user has a selection at the time the
      // BLOCK ribbon is clicked, inject it as `targetEntityIds` so the
      // definition flow skips `block-select-objects` and lands on
      // `block-base-point` directly (matches AutoCAD's selection-first
      // behaviour for the BLOCK command).
      "BLOCK",
    ] as const;
    if (
      (MODIFY_COMMANDS_WITH_PRESELECTION as readonly string[]).includes(
        command,
      ) &&
      !resolvedOptions?.targetEntityIds &&
      state.selection.entityIds.length > 0
    ) {
      resolvedOptions = {
        ...(resolvedOptions ?? {}),
        targetEntityIds: JSON.stringify(state.selection.entityIds),
      };
    }

    // BEDIT (Phase 2.3) — seed the session with the current list of
    // known block names so the state machine can tell existing from
    // new names without another store round-trip. Also drop a hint
    // flag the bedit-prompt-name step can read to surface `?` → list.
    if (command === "BEDIT") {
      resolvedOptions = {
        ...(resolvedOptions ?? {}),
        blockNames: JSON.stringify(
          Object.keys(geometryDocument.blocks ?? {}).filter(
            (name) => !name.startsWith("*"),
          ),
        ),
      };
    }

    // DDATTE / ATTEDIT / EATTEDIT pre-arming (Phase 2.2):
    // if a block INSERT is already selected, skip the select step and
    // go directly to the dialog by seeding the session with
    // `targetEntityId`.
    if (
      (command === "DDATTE" ||
        command === "ATTEDIT" ||
        command === "EATTEDIT") &&
      !resolvedOptions?.targetEntityId
    ) {
      const firstSelectedInsert = state.drawing.entities.find(
        (entity) =>
          state.selection.entityIds.includes(entity.id) &&
          entity.type === "Block",
      );
      if (firstSelectedInsert) {
        resolvedOptions = {
          ...(resolvedOptions ?? {}),
          targetEntityId: firstSelectedInsert.id,
        };
      }
    }

    // VPCLIP-INTERACTIVE pre-arming — AutoCAD parity: VPCLIP with a SINGLE
    // viewport already selected skips "Select viewport to clip:" and starts
    // directly at the first-corner prompt (mirrors the DDATTE-family
    // single-target pre-arm immediately above). 0 or 2+ viewports selected
    // leaves the interactive select step in place (an ambiguous target is
    // not silently guessed).
    //
    // @codex round-4 P2 — `state.drawing.entities` is intentionally EMPTY
    // above `SHELL_ENTITY_PREVIEW_THRESHOLD` (large cache-backed imports),
    // so scanning it (the DDATTE-family pattern this was modelled on)
    // silently never pre-arms on big drawings even with a viewport
    // selected. `geometryDocument.spaces` is the full typed source,
    // unaffected by that preview truncation (same source DIMBASELINE's
    // own pre-arm below already uses).
    //
    // @codex round-5 P2 — classify against every ACTIVE viewport in the
    // target layout first, THEN intersect with the selection, rather than
    // feeding just the selected subset to `excludeControlViewport` directly.
    // (Historical note: this was originally load-bearing for round-5's
    // largest-area control-VP fallback, which depended on seeing the FULL
    // layout to judge "largest of the set" correctly — a selected SUBSET of
    // two same-layout viewports would have misclassified the uniquely-larger
    // one. Round-11 removed that fallback entirely — `excludeControlViewport`
    // is now explicit-tag-only and per-item, so full-set-vs-subset no longer
    // changes its output — but classify-full-then-intersect is kept as the
    // simpler, still-correct shape rather than churning this working code.)
    if (command === "VPCLIP" && !resolvedOptions?.vpclipViewportId) {
      const targetLayoutSpace = geometryDocument.spaces.find(
        (s) => s.id === targetSpaceId,
      );
      const allActiveViewportsInLayout = (
        targetLayoutSpace?.entities ?? []
      ).filter(
        (entity): entity is ViewportEntity =>
          entity.type === "Viewport" && entity.viewportStatus > 0,
      );
      const modelWindowViewports = excludeControlViewport(
        allActiveViewportsInLayout,
      );
      const selectedViewports = modelWindowViewports.filter((vp) =>
        state.selection.entityIds.includes(vp.id),
      );
      if (selectedViewports.length === 1) {
        resolvedOptions = {
          ...(resolvedOptions ?? {}),
          vpclipViewportId: selectedViewports[0]!.id,
        };
      }
    }

    // MLEADEREDIT ribbon entry
    if (command === "MLEADEREDIT" && !resolvedOptions?.targetEntityId) {
      const firstSelectedMleader = state.drawing.entities.find(
        (entity) =>
          state.selection.entityIds.includes(entity.id) &&
          entity.type === "Multileader",
      );
      if (firstSelectedMleader) {
        resolvedOptions = {
          ...(resolvedOptions ?? {}),
          targetEntityId: firstSelectedMleader.id,
        };
      }
    }

    // DIMBASELINE / DIMCONTINUE pre-arming — chain off the most recently
    // created Dimension entity (linear or aligned). Walk the geometry
    // document for the most recent Dimension and seed session.options
    // chain* fields so the dim state machine can compute each next dim
    // anchor on each click.
    if (
      (command === "DIMBASELINE" || command === "DIMCONTINUE") &&
      !resolvedOptions?.chainExt1X
    ) {
      const allEntities = geometryDocument.spaces.flatMap((s) => s.entities);
      let lastDim: (typeof allEntities)[number] | undefined;
      for (let i = allEntities.length - 1; i >= 0; i -= 1) {
        const candidate = allEntities[i];
        if (
          candidate?.type === "Dimension" &&
          (candidate.dimensionType === 0 || candidate.dimensionType === 1)
        ) {
          lastDim = candidate;
          break;
        }
      }
      if (lastDim && lastDim.type === "Dimension") {
        const anchorPoint =
          command === "DIMCONTINUE"
            ? lastDim.extLine2Point
            : lastDim.extLine1Point;
        resolvedOptions = {
          ...(resolvedOptions ?? {}),
          chainExt1X: String(anchorPoint.x),
          chainExt1Y: String(anchorPoint.y),
          chainExt2X: String(lastDim.extLine2Point.x),
          chainExt2Y: String(lastDim.extLine2Point.y),
          chainDimLineX: String(lastDim.defPoint.x),
          chainDimLineY: String(lastDim.defPoint.y),
          chainDimType: String(lastDim.dimensionType),
          chainStep: "0",
        };
      } else {
        appendCommandFeed(
          t(
            command === "DIMBASELINE"
              ? "prompt.dimbaseline.no_anchor"
              : "prompt.dimcontinue.no_anchor",
          ),
        );
        return;
      }
    }

    // Remove-leader safety net
    if (
      command === "MLEADEREDIT" &&
      resolvedOptions?.targetEntityId &&
      resolvedOptions?.mode === "remove"
    ) {
      const targetId = resolvedOptions.targetEntityId;
      const targetGeomEntity = geometryDocument.spaces
        .flatMap((space) => space.entities)
        .find((entity) => entity.id === targetId);
      if (targetGeomEntity && targetGeomEntity.type === "Multileader") {
        const totalLines = targetGeomEntity.leaderRoots.reduce(
          (sum, root) =>
            sum +
            root.nodes.reduce(
              (nodeSum, node) => nodeSum + node.lines.length,
              0,
            ),
          0,
        );
        if (totalLines <= 1) {
          const ok =
            typeof window !== "undefined"
              ? window.confirm(t("confirm.mleaderedit.last_leader"))
              : true;
          if (!ok) {
            appendCommandFeed(t("feed.esc"));
            return;
          }
          dispatch({
            type: "geometry/remove-leader-from-multileader",
            source: "toolbar",
            entityId: targetId,
            leaderIndex: 0,
            confirmed: true,
          });
          return;
        }
      }
    }

    setDraftSession(startDraftSession(command, targetSpaceId, resolvedOptions));
    setCommandText("");
    setCommandHistoryOpen(false);
    appendCommandFeed(command);

    dispatch({
      type: "commandline/submit",
      source: "toolbar",
      text: t("drafting.armed", { command, space: targetSpaceName }),
      // Bare-command-entry audit-tone fix — this dispatch exists ONLY to log
      // the "<command> armed in <space>." confirmation into the audit trail;
      // `text` is an already-resolved status message, not raw command-line
      // input. Without `resolvedTone`, `routeLayoutCommand` (editorStore.ts)
      // reparsed it as candidate command text — for LINE/POINT/CIRCLE/ARC the
      // trailing "armed in <space>." words fail THAT command's coordinate
      // grammar, so the confirmation was silently replaced by an `error`-tone
      // "<CMD> expects …" audit row (AutoCAD parity violation: typing a bare
      // command name starts the command and prompts for input — it is never
      // a rejection). See `commandline/submit.resolvedTone`'s doc comment
      // (packages/contracts/src/editor.ts) for the full contract.
      resolvedTone: "info",
    });
  }

  function handleStagePoint(point: DraftPoint, spaceId: string) {
    // Idle-state selection (entity pick + empty-click clear) is fully
    // owned by StagePointerHandlers.handleClickPick — see
    // apps/web/src/app/scene3d/scene/StagePointerHandlers.ts:158. We
    // forward the canvas-point only when a draft session is the active
    // consumer; otherwise it would race-clobber StagePointerHandlers'
    // dispatch.
    //
    // Read the LATEST session from the ref, not the closure-captured
    // `draftSession`. The canvas pointer listener (stageInteractions) can
    // fire with a closure from a render that predates `beginDrafting`'s
    // state commit — e.g. the FIRST corner click of a freshly-started MVIEW
    // arrives before React has re-rendered this hook with the new session,
    // so the closure `draftSession` is still null/stale and the point is
    // silently dropped. The ref is synced every render (draftSessionRef),
    // matching the proven text-submission path.
    const liveSession = draftSessionRef.current;
    if (!liveSession || liveSession.spaceId !== spaceId) {
      return;
    }

    handleDraftPoint(point, "scene");
  }

  function handleStageHover(
    point: DraftPoint | null,
    spaceId: string,
    entityId: string | null = null,
  ) {
    // REVCLOUD Freehand (REVCLOUD-MODES) — while the freehand step is
    // active, hover points feed the path sampler instead of the plain
    // hoverPoint patch. The sampler appends a vertex every ~arc length
    // of cursor travel and COMMITS the cloud when the cursor returns to
    // the start point (AutoCAD auto-close). Side effects run outside
    // any setState updater — same ref + commit-outside pattern as
    // handleDraftPoint.
    const freehandSession = draftSessionRef.current;
    if (
      point &&
      freehandSession &&
      freehandSession.spaceId === spaceId &&
      freehandSession.step === "revcloud-freehand-path"
    ) {
      const freehandResult = advanceRevcloudFreehandHover(
        freehandSession,
        point,
      );
      if (freehandResult) {
        if (freehandResult.commit) {
          commitDraftedEntity(freehandResult.commit);
        }
        draftSessionRef.current = freehandResult.nextSession;
        setDraftSession(freehandResult.nextSession);
        return;
      }
    }

    setDraftSession((currentSession) =>
      currentSession && currentSession.spaceId === spaceId
        ? {
            ...currentSession,
            hoverPoint: point,
          }
        : currentSession,
    );

    // AutoCAD SELECTIONPREVIEW gate. SELECTIONPREVIEW bit 1 = preview
    // when no command active; bit 2 = preview during selection-prompt.
    // We only suppress hover when SELECTIONPREVIEW is 0 (no preview) —
    // bit logic at the renderer side cares about command-mode context;
    // for the writer side it's enough to skip when preview is fully
    // disabled. The dispatcher is also responsible for filtering out
    // entities whose category is masked by PREVIEWFILTER.
    const hoverStore = useStageHoverStore.getState();
    const prefs = getPreviewPreferences();
    if (prefs.selectionPreview === 0) {
      hoverStore.setHover(null, null, spaceId);
      return;
    }

    let allowedEntityId: string | null = entityId;
    if (allowedEntityId !== null) {
      const entity = getEntityById(allowedEntityId);
      if (!entity) {
        allowedEntityId = null;
      } else {
        // PREVIEWFILTER (default 7 = locked layer | xrefs | tables).
        // Skip hover highlight for filtered entities so the renderer
        // never paints them — matches AutoCAD parity.
        const layerName = entity.layer;
        const filter = prefs.previewFilter;
        if (
          (filter & PREVIEW_FILTER_LOCKED_LAYER) !== 0 &&
          isLayerLocked(layerName)
        ) {
          allowedEntityId = null;
        } else if (
          (filter & PREVIEW_FILTER_XREFS) !== 0 &&
          isXrefEntity(entity)
        ) {
          allowedEntityId = null;
        } else if (
          (filter & PREVIEW_FILTER_TABLES) !== 0 &&
          isTableEntity(entity)
        ) {
          allowedEntityId = null;
        }
      }
    }
    hoverStore.setHover(allowedEntityId, null, spaceId);
  }

  function handleEntityDoubleClick(entityId: string) {
    const entity = state.drawing.entities.find((e) => e.id === entityId);
    if (!entity) return;

    // Double-click on Viewport → enter MSPACE
    if (entity.type === "Viewport") {
      dispatch({
        type: "shell/set-layout-context",
        source: "scene",
        layoutContext: "viewport",
      });
      return;
    }

    // Phase J.1 — double-click on Block (INSERT) enters BEDIT for the
    // underlying definition (AutoCAD parity). Guarded:
    //  - skip when an active draft session is in progress (don't yank
    //    the user out of LINE/CIRCLE/etc.)
    //  - skip when multiple blocks are selected at the time (ambiguous
    //    — the user may be doing a window-select drag-double-click)
    //  - skip anonymous / system blocks (`*`-prefixed) — these are
    //    layout / xref / dimension owner records, not user blocks
    //  - skip when already inside BEDIT (recursive open is a no-op)
    if (entity.type === "Block") {
      if (draftSessionRef.current !== null) return;
      const blockNameRaw = entity.properties["Name"];
      const blockName =
        typeof blockNameRaw === "string" ? blockNameRaw.trim() : "";
      if (!blockName || blockName.startsWith("*")) return;
      const activeGeoSpace = geometryDocument.spaces.find(
        (s) => s.id === geometryDocument.activeSpaceId,
      );
      if (activeGeoSpace?.kind === "block-edit") return;
      // Disambiguate multi-selection: if more than one entity is
      // selected at the time of the double-click, defer to user intent
      // (window-select drag-release double-click should not enter BEDIT
      // unexpectedly).
      if (state.selection.entityIds.length > 1) return;
      dispatch({
        type: "space/enter-block-edit",
        source: "scene",
        blockName,
        createIfMissing: false,
      });
      appendCommandFeed(t("summary.bedit_entered", { name: blockName }));
      return;
    }

    if (
      entity.type !== "Text" &&
      entity.type !== "MText" &&
      entity.type !== "Multileader"
    )
      return;

    const contents =
      entity.properties.Contents ?? entity.properties["Contents"] ?? "";
    setInPlaceEditState({ entityId, text: contents });
  }

  function applyStageSelection(
    entityIds: string[],
    spaceId: string,
    additive = false,
  ) {
    // An EMPTY pick (clicked empty space) must clear selection but NEVER
    // switch the active model/paper space (AutoCAD parity: clicking empty
    // space never changes the active context). This is also critical for
    // point-specifying drafts: the click-pick selection runs on its own
    // parallel pointerup listener (StagePointerHandlers) alongside the draft
    // point handler, and MVIEW's viewport corners are empty clicks. Without
    // this guard, the empty pick resolves spaceId to "model" and fires
    // activate-space → the active tab flips layout→model. On non-final
    // corners that drops the next point (handleStagePoint guard mismatch);
    // on the FINAL corner (draft already ended, so a draftSession guard is
    // racy) it flips to model right after create-viewport commits — the
    // paper sheet + new viewport vanish from view though the VP entity
    // exists. Empty clicks therefore short-circuit here with no space change.
    if (entityIds.length === 0) {
      dispatch({
        type: "shell/clear-selection",
        source: "scene",
      });
      return;
    }

    // Non-empty pick: switch to the picked entity's space if needed — but
    // not while a draft session owns the click (the draft keeps its space).
    //
    // MC4 S2 P1 — in MSPACE_VIA_VP an in-VP pick resolves the model entity in
    // the active VP's model space and dispatches `spaceId: "model"`, which
    // differs from the underlying `drawing.activeSpaceId` (the layout space).
    // Activating "model" here would flip the active tab layout→model and yank
    // the user OUT of the layout/MSPACE_VIA_VP context — but in that mode the
    // active space intentionally STAYS the layout (the model is being edited
    // *through* the VP, not via the Model tab). This mirrors the identical
    // suppression in `beginDrafting` (MC4 iter-3 P1 #165): the draft targets
    // model space but must not dispatch `shell/activate-space`. Suppress the
    // activation here for the same reason so an in-VP pick selects the model
    // entity IN PLACE (selection set + VP highlight update) without changing
    // the active tab. Outside MSPACE_VIA_VP (PSPACE / plain MSPACE / normal
    // model/paper pick) the activation runs unchanged.
    const spaceContextSnapshot = useSpaceContextStore.getState();
    const spaceContextMode = spaceContextSnapshot.mode;
    if (
      spaceId !== state.drawing.activeSpaceId &&
      !draftSessionRef.current &&
      spaceContextMode !== "MSPACE_VIA_VP"
    ) {
      dispatch({
        type: "shell/activate-space",
        source: "scene",
        spaceId,
      });
    }

    dispatch({
      type: "shell/select-entities",
      source: "scene",
      entityIds: additive
        ? Array.from(new Set([...state.selection.entityIds, ...entityIds]))
        : entityIds,
    });

    // MC4 S2 P1 (iter-3 #A, hardened iter-5 #1) — PRESERVE the MSPACE_VIA_VP
    // session across an in-VP selection.
    //
    // The MSPACE_VIA_VP mode + active VP live on `spaceContextStore`, set
    // directly by `viewportContextController.enterViewport` on the VP
    // double-click. The `shell/select-entities` dispatch above routes through
    // `editorStore.dispatch → syncShellStateFromGeometryDocument`.
    //
    // ROOT FIX (iter-5 #1, storeHelpers.ts): that resync now emits
    // `drawing.layoutContext: "viewport"` (instead of collapsing the layout
    // active space to `"paper"`) whenever the space-context store is
    // MSPACE_VIA_VP over a VP that still exists in the active layout. On the
    // next render `App.tsx`'s `syncFromEditor("viewport")` effect maps that to
    // MSPACE_VIA_VP — NOT PSPACE — and never calls `exitViewport`, so the mode
    // survives the resync at the source for BOTH this direct path and the
    // cycling-dialog path (which dispatches `shell/select-entities` directly).
    //
    // This `reassertViewportSession` call is retained as an idempotent
    // belt-and-suspenders guard: with the root fix it is a pure no-op (the
    // store is already MSPACE_VIA_VP over the same VP, so neither
    // `enterViewport` nor `exitViewport` runs). It still recovers the session
    // if any future path momentarily knocks the store off the VP between the
    // dispatch and the next render. Outside MSPACE_VIA_VP it is a no-op — a
    // normal (non-VP) selection is unchanged.
    if (
      spaceContextMode === "MSPACE_VIA_VP" &&
      spaceContextSnapshot.activeViewportId !== null
    ) {
      reassertViewportSession(spaceContextSnapshot.activeViewportId);
    }
  }

  /**
   * MC4 S2 P1 (iter-3 #A) — re-assert an MSPACE_VIA_VP session on
   * `spaceContextStore` after a dispatch that may have collapsed it to PSPACE
   * via the layout-context resync (see `applyStageSelection`).
   *
   * Idempotent: when the store already reports `MSPACE_VIA_VP` over the same VP
   * (the common case — the resync effect did not re-fire) this is a no-op.
   * When the store was knocked back to PSPACE, `enterViewport` (which requires
   * PSPACE) re-enters the captured VP. When it was knocked to some OTHER VP /
   * mode we exit first, then re-enter, mirroring
   * `viewportContextController.doEnterViewport`'s VP-switch sequence.
   */
  function reassertViewportSession(viewportId: string): void {
    const store = useSpaceContextStore.getState();
    if (
      store.mode === "MSPACE_VIA_VP" &&
      store.activeViewportId === viewportId
    ) {
      return; // already intact — nothing to do.
    }
    if (store.mode === "MSPACE_VIA_VP") {
      // Knocked onto a different VP — exit first so enterViewport's PSPACE
      // precondition holds, then re-target the intended VP.
      store.exitViewport();
    }
    useSpaceContextStore.getState().enterViewport(viewportId);
  }

  function selectEntityFromStage(
    entityId: string,
    spaceId: string,
    additive = false,
  ) {
    applyStageSelection([entityId], spaceId, additive);
  }

  function selectEntitiesFromStage(
    entityIds: string[],
    spaceId: string,
    additive = false,
  ) {
    applyStageSelection(entityIds, spaceId, additive);
  }

  return {
    draftSession,
    setDraftSession,
    liveCanvasText,
    setLiveCanvasText,
    inPlaceEditState,
    setInPlaceEditState,
    zoomPickingPrompt,
    setZoomPickingPrompt,
    beginDrafting,
    handleDraftPoint,
    handleDraftTextSubmission,
    handleStagePoint,
    handleStageHover,
    handleEntityDoubleClick,
    commitDraftedEntity,
    selectEntityFromStage,
    selectEntitiesFromStage,
  };
}
